import { getApiRoot, getRuntimeConfig } from '../config/runtimeConfig';
import { createHttpClient } from './httpClient';
import { createBackendContract, isAdminPath } from './compat/backendContract';
import { readStoredPlayerAssignment } from './playerSessionStorage';

const ADMIN_UNAUTHORIZED_EVENT = 'admin:unauthorized';

function emitAdminUnauthorized() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(ADMIN_UNAUTHORIZED_EVENT));
  }
}

function buildDefaultHeaders(runtimeConfig, { playerAuth = false } = {}) {
  const headers = {
    'Content-Type': 'application/json'
  };
  const hasValidDnnId = (value) => Number.isFinite(Number(value)) && Number(value) > 0;

  if (playerAuth) {
    const assignment = readStoredPlayerAssignment();
    if (assignment?.playerToken) {
      headers.Authorization = `Bearer ${assignment.playerToken}`;
    }
  }

  if (hasValidDnnId(runtimeConfig?.moduleId)) {
    headers.ModuleId = String(runtimeConfig.moduleId);
  }

  if (hasValidDnnId(runtimeConfig?.tabId)) {
    headers.TabId = String(runtimeConfig.tabId);
  }

  if (hasValidDnnId(runtimeConfig?.tabModuleId)) {
    headers.TabModuleId = String(runtimeConfig.tabModuleId);
  }

  if (runtimeConfig?.antiForgeryToken) {
    headers.RequestVerificationToken = runtimeConfig.antiForgeryToken;
  }

  return headers;
}

function createCompatApi(request, backendContract) {
  const { mode, session, game, adminAuth, admin } = backendContract;
  const isDnn = mode === 'dnn';

  return {
    getSessionStatus: () => request(session.status),
    getSessionMe: () => request(session.me, { playerAuth: true }),
    joinSession: (name) => request(session.join, { method: 'POST', body: JSON.stringify({ name }) }),

    submitAnswer: (body) => request(game.answer, { method: 'POST', body: JSON.stringify(body), playerAuth: true }),
    unlockRound: (secretWord) => request(game.unlock, { method: 'POST', body: JSON.stringify({ secretWord }), playerAuth: true }),
    startGame: () => request(game.start, { method: 'POST' }),
    pauseGame: () => request(game.pause, { method: 'POST' }),
    resetGame: () => request(game.reset, { method: 'POST' }),
    stopGame: () => request(game.stop, { method: 'POST' }),

    adminLogin: (username, password) => request(adminAuth.login, { method: 'POST', body: JSON.stringify({ username, password }) }),
    getAdminMe: () => request(adminAuth.me),

    getAdminBootstrap: () => request(admin.bootstrap),
    getAdminGameStatus: () => request(admin.gameStatus),
    getAdminGameLog: () => request(admin.gameLog),
    updateDraftQuiz: (body) => request(admin.draftQuiz, { method: 'PUT', body: JSON.stringify(body) }),

    getQuestionSets: () => request(admin.questionSets),
    getQuestionSet: (id) => request(isDnn ? `${admin.questionSets}?id=${id}` : `${admin.questionSets}/${id}`),
    createQuestionSet: (body) => request(admin.questionSetCreate, { method: 'POST', body: JSON.stringify(body) }),
    updateQuestionSet: (id, body) => request(isDnn ? `${admin.questionSetUpdate}?id=${id}` : `${admin.questionSetUpdate}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteQuestionSet: (id) => request(isDnn ? `${admin.questionSetDelete}?id=${id}` : `${admin.questionSetDelete}/${id}`, { method: 'DELETE' }),

    createQuestion: (setId, body) => request(isDnn ? `${admin.questionCreate}?setId=${setId}` : `${admin.questionCreate}/${setId}/questions`, { method: 'POST', body: JSON.stringify(body) }),
    updateQuestion: (setId, questionId, body) => request(isDnn ? `${admin.questionUpdate}?setId=${setId}&questionId=${questionId}` : `${admin.questionUpdate}/${setId}/questions/${questionId}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteQuestion: (setId, questionId) => request(isDnn ? `${admin.questionDelete}?setId=${setId}&questionId=${questionId}` : `${admin.questionDelete}/${setId}/questions/${questionId}`, { method: 'DELETE' }),

    getQuizzes: () => request(admin.quizzes),
    saveQuiz: (body) => request(admin.quizCreate, { method: 'POST', body: JSON.stringify(body) }),
    updateQuiz: (id, body) => request(isDnn ? `${admin.quizUpdate}?id=${id}` : `${admin.quizUpdate}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    loadQuizToDraft: (id) => request(isDnn ? `${admin.quizLoad}?id=${id}` : `${admin.quizLoad}/${id}/load`, { method: 'POST' }),
    deleteQuiz: (id) => request(isDnn ? `${admin.quizDelete}?id=${id}` : `${admin.quizDelete}/${id}`, { method: 'DELETE' }),

    getGlobalSettings: () => request(admin.globalSettings),
    uploadGlobalAsset: (slot, body) => request(isDnn ? `${admin.globalSettingsAssetUpload}?slot=${slot}` : `${admin.globalSettingsAssetUpload}/${slot}`, { method: 'POST', body: JSON.stringify(body) }),
    deleteGlobalAsset: (slot) => request(isDnn ? `${admin.globalSettingsAssetDelete}?slot=${slot}` : `${admin.globalSettingsAssetDelete}/${slot}`, { method: 'DELETE' })
  };
}

export function createApiClient({ runtimeConfig = getRuntimeConfig() } = {}) {
  const backendContract = createBackendContract(runtimeConfig);
  const hasValidDnnId = (value) => Number.isFinite(Number(value)) && Number(value) > 0;
  const appendDnnContext = (path) => {
    if (backendContract.mode !== 'dnn') return path;

    const params = new URLSearchParams();
    if (hasValidDnnId(runtimeConfig?.moduleId)) params.set('moduleid', String(runtimeConfig.moduleId));
    if (hasValidDnnId(runtimeConfig?.tabId)) params.set('tabid', String(runtimeConfig.tabId));
    if (hasValidDnnId(runtimeConfig?.tabModuleId)) params.set('tabmoduleid', String(runtimeConfig.tabModuleId));
    const query = params.toString();
    if (!query) return path;

    return `${path}${path.includes('?') ? '&' : '?'}${query}`;
  };

  const httpClient = createHttpClient({
    baseUrl: backendContract.baseUrl,
    getDefaultHeaders: (options) => buildDefaultHeaders(runtimeConfig, options),
    onUnauthorized: ({ path }) => {
      if (isAdminPath(path, backendContract)) {
        emitAdminUnauthorized();
      }
    }
  });

  const request = (path, options = {}) => httpClient.request(appendDnnContext(path), options);
  return createCompatApi(request, backendContract);
}

export function getApiBase() {
  const backendContract = createBackendContract(getRuntimeConfig());
  return backendContract.baseUrl;
}

export function getAssetBase() {
  return getApiRoot();
}

export function resolveAssetUrl(assetPath = '') {
  if (!assetPath) return '';
  if (/^https?:\/\//i.test(assetPath)) return assetPath;
  if (assetPath.startsWith('/')) return assetPath;
  return `${getAssetBase()}/${assetPath.replace(/^\/+/, '')}`;
}

function getLiveApiClient() {
  return createApiClient({ runtimeConfig: getRuntimeConfig() });
}

export const apiClient = new Proxy(
  {},
  {
    get(_target, prop) {
      const client = getLiveApiClient();
      const value = client[prop];
      return typeof value === 'function' ? value.bind(client) : value;
    }
  }
);

export { ADMIN_UNAUTHORIZED_EVENT };
